import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Input, Select, Button, Popover, notification, Spin, Tooltip } from "antd";
import { ArrowLeftOutlined, MailOutlined, ReloadOutlined } from "@ant-design/icons";
import { debounce } from "lodash";
import { listTemplates, getTemplate, previewCustomEmail, testSendCustomEmail, sendCustomEmail } from "../../Services/customEmail";
import VariableRow from "./components/VariableRow";
import RemoteSelect from "./components/RemoteSelect";
import EmailPreview from "./components/EmailPreview";
import { fetchUsers, pasteUsers } from "./fetchers";
import { CePage } from "./styles";
import { emptyVariableState, isEmpty, mustFill, itinKeys, neededItinKeys, toPayloadVariables, renderTemplate, LIMIT_BYTES, fmtDay } from "./logic";

const Pill = ({ s }) => <span className={`pill ${s.cls}`}>{s.cls === "ok" ? "✓ " : s.cls ? "! " : ""}{s.txt}</span>;
const SectionHead = ({ n, title, desc, status }) => (
  <div className="shead">
    <span className="sn">{n}</span>
    <div><h3>{title}</h3>{desc && <div className="desc">{desc}</div>}</div>
    {status && <Pill s={status} />}
  </div>
);
const userOption = (o) => (
  <div style={{ lineHeight: 1.3 }}><div>#{o.value} {o.name}</div><div style={{ fontSize: 12, color: "#5f6680" }}>{o.email}</div></div>
);
const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// The form is kept in sessionStorage so a page reload doesn't lose what was filled in. Leaving the
// page (or sending the campaign) clears it, so Create always opens empty.
const DRAFT_KEY = "custom_email_draft";
const readDraft = () => {
  try { return JSON.parse(sessionStorage.getItem(DRAFT_KEY)) || null; } catch { return null; }
};

const CreateCustomEmail = () => {
  const navigate = useNavigate();
  const draft = useRef(readDraft());
  const [title, setTitle] = useState(draft.current?.title || "");
  const [templateId, setTemplateId] = useState(draft.current?.templateId || null);
  const [vars, setVars] = useState({});
  const [varsFor, setVarsFor] = useState(null); // the template id `vars` belongs to
  const [recipients, setRecipients] = useState(draft.current?.recipients || []);
  const [focus, setFocus] = useState(null); // { key, at }: the variable being edited, shown in the preview
  const [mode, setMode] = useState("desktop");
  const [preview, setPreview] = useState(null);
  const [previewError, setPreviewError] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [testing, setTesting] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirming, setConfirming] = useState(false);

  // OneSignal templates, 50 at a time; the next 50 load when the dropdown is scrolled to the end
  const tplPages = useInfiniteQuery({
    queryKey: ["custom_email_templates"], queryFn: ({ pageParam }) => listTemplates(pageParam),
    initialPageParam: 0, getNextPageParam: (last) => last.next_offset ?? undefined, refetchOnWindowFocus: false, retry: false,
  });
  const templates = useMemo(() => (tplPages.data?.pages || []).flatMap((pg) => pg.templates), [tplPages.data]);
  const { data: template, isFetching: loadingTemplate } = useQuery({
    queryKey: ["custom_email_template", templateId], queryFn: () => getTemplate(templateId),
    enabled: !!templateId, refetchOnWindowFocus: true, retry: false,
  });

  // A new template -> fresh variable state (types preselected from the last send). The same template
  // loaded again (window focus, reload) keeps what was already filled in.
  useEffect(() => {
    if (!template) return;
    const saved = draft.current?.templateId === template.id ? draft.current.vars || {} : {};
    draft.current = null; // the draft is used once, on the first load
    const same = varsFor === template.id;
    setVars((prev) => Object.fromEntries(template.variables.map((i) => {
      const kept = same ? prev[i.key] : saved[i.key];
      const state = kept ? { ...emptyVariableState(i), ...kept } : emptyVariableState(i);
      // a variable with only one possible type (e.g. preheader-only = Text) always has that type,
      // even when the kept / restored value was saved without one
      return [i.key, i.types.length === 1 ? { ...state, type: i.types[0] } : state];
    })));
    setVarsFor(template.id);
  }, [template]); // eslint-disable-line react-hooks/exhaustive-deps -- runs when the template (re)loads

  const recipientIds = recipients.map((r) => r.value);
  // the preview uses the first recipient's values, or sample values until a recipient is added
  const previewUser = recipients[0] || null;
  const previewUserValue = previewUser?.value ?? null;
  const itinCount = itinKeys(template, vars).length;

  useEffect(() => {
    if (templateId && varsFor !== templateId) return; // the template's fields haven't loaded (or been restored) yet
    try {
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ title, templateId, recipients, vars: templateId ? vars : {} }));
    } catch { /* storage full or blocked: the form still works */ }
  }, [title, templateId, recipients, vars, varsFor]);
  // runs when the page is left (not on reload - a reload never unmounts)
  useEffect(() => () => { try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ } }, []);

  // live preview: backend builds this user's custom_data (same code as the real send)
  const payloadVars = useMemo(() => (template ? toPayloadVariables(template, vars) : []), [template, vars]);
  const previewReq = useRef(0);
  const runPreview = useMemo(() => debounce(async (tplId, variables, userId) => {
    const id = ++previewReq.current; // only the latest request may update the preview
    try {
      const res = await previewCustomEmail({ template_id: tplId, variables, preview_user_id: userId });
      if (id !== previewReq.current) return;
      setPreview(res);
      setPreviewError(null);
    } catch (e) {
      if (id === previewReq.current) setPreviewError(e.apiMessage || e.message);
    } finally {
      if (id === previewReq.current) setPreviewLoading(false);
    }
  }, 450), []);
  useEffect(() => () => runPreview.cancel(), [runPreview]);
  useEffect(() => {
    if (!template || varsFor !== template.id) return;
    setPreviewLoading(true); // a preview error from older inputs is ignored until this one answers
    runPreview(template.id, payloadVars, previewUserValue);
  }, [template, payloadVars, previewUserValue, runPreview, varsFor]);

  // ---- validation (same rules as the backend; the backend checks again on send)
  const varProblems = template ? template.variables.filter((i) => vars[i.key] && mustFill(i, vars) && isEmpty(vars[i.key])) : [];
  const urlProblems = template
    ? template.variables.filter((i) => { const v = vars[i.key]; return v?.type === "url" && v.url_sub_type === "custom" && v.value?.trim() && !/^https?:\/\//i.test(v.value.trim()); })
    : [];
  const mapProblems = [];
  if (template) {
    itinKeys(template, vars).forEach((key) => {
      const rows = vars[key].mappings;
      rows.forEach((r, i) => {
        if (!r.users.length || !r.itinerary) mapProblems.push(`${key}: row ${i + 1} needs users and one itinerary.`);
      });
      const seen = new Set();
      rows.flatMap((r) => r.users).forEach((u) => {
        if (seen.has(u.value)) mapProblems.push(`${key}: ${u.name || u.label} is in more than one row.`);
        seen.add(u.value);
      });
    });
    neededItinKeys(template, vars).forEach((key) => {
      const mapped = new Set(vars[key].mappings.flatMap((r) => r.users.map((u) => u.value)));
      const missing = recipients.filter((r) => !mapped.has(r.value));
      if (missing.length) mapProblems.push(`${key}: pick an itinerary for ${missing.slice(0, 3).map((r) => r.name || r.label).join(", ")}${missing.length > 3 ? ` and ${missing.length - 3} more` : ""}.`);
    });
  }
  const mappedNotRecipients = template
    ? [...new Map(itinKeys(template, vars).flatMap((k) => vars[k].mappings.flatMap((r) => r.users))
        .filter((u) => !recipientIds.includes(u.value)).map((u) => [u.value, u])).values()]
    : [];
  const noSubject = template && !template.subject;
  const tooBig = preview && preview.size_bytes > LIMIT_BYTES;
  const blockers = [
    !templateId && "Pick a OneSignal template.",
    noSubject && "This template has no subject in OneSignal. Add one there first.",
    !title.trim() && "Add a campaign title.",
    varProblems.length && `${varProblems.length} required variable${varProblems.length > 1 ? "s need" : " needs"} a value: ${varProblems.map((i) => i.key).join(", ")}.`,
    ...urlProblems.map((i) => `${i.key}: the URL must start with http:// or https://.`),
    ...mapProblems,
    !recipients.length && "Add at least one recipient.",
    tooBig && "The email content is too long for OneSignal (over 10 KB). Shorten the text or HTML.",
    previewError && !previewLoading && !varProblems.length && !urlProblems.length && !mapProblems.length && previewError,
  ].filter(Boolean);
  const canSend = templateId && template && !blockers.length;

  const varCount = varProblems.length + urlProblems.length;
  const status = {
    template: !templateId ? { cls: "warn", txt: "Pick a template" } : noSubject ? { cls: "bad", txt: "No subject" } : !title.trim() ? { cls: "warn", txt: "Add a title" } : { cls: "ok", txt: "Ready" },
    variables: !template ? { cls: "", txt: "Pick a template" } : !template.variables.length ? { cls: "", txt: "None in template" } : varCount ? { cls: "warn", txt: `${varCount} to fill` } : { cls: "ok", txt: "Ready" },
    recipients: !recipients.length ? { cls: "warn", txt: "Add users" } : mapProblems.length ? { cls: "warn", txt: `${mapProblems.length} to fix` } : { cls: "ok", txt: plural(recipients.length, "user") },
    send: canSend ? { cls: "ok", txt: "Ready to send" } : { cls: "", txt: "Not ready" },
  };
  useEffect(() => { if (!canSend) setConfirming(false); }, [canSend]);

  // While the send is running, warn before the tab is closed or reloaded
  useEffect(() => {
    if (!sending) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [sending]);

  const testEmailOk = EMAIL_RE.test(testEmail.trim());
  const doTest = async () => {
    if (!testEmailOk || testing) return;
    setTesting(true);
    try {
      const res = await testSendCustomEmail({ template_id: templateId, variables: payloadVars, preview_user_id: previewUserValue, email: testEmail.trim() });
      notification.success({ message: `Test email sent to ${res.email}`, description: "Links in a test email aren't tracked and nothing is saved." });
      setTestOpen(false);
    } catch (e) {
      notification.error({ message: "Test email not sent", description: e.apiMessage || e.message });
    } finally {
      setTesting(false);
    }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const res = await sendCustomEmail({ title: title.trim(), template_id: templateId, variables: payloadVars, recipient_user_ids: recipientIds });
      notification.success({ message: res.message, description: res.sent_count < res.total ? "Open the recipients list to see why some emails weren't sent." : undefined });
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
      navigate(`/email_campaign/custom/${res.campaign_id}`);
    } catch (e) {
      notification.error({ message: "Campaign not sent", description: e.apiMessage || e.message, duration: 6 });
      setSending(false);
    }
  };

  const testPopover = (
    <CePage style={{ width: 340, display: "grid", gap: 10 }}>
      <label htmlFor="ce-test" className="note" style={{ fontWeight: 600 }}>Send a test copy to</label>
      <Input id="ce-test" aria-label="Test email address" type="email" value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="name@example.com" onPressEnter={doTest}
        status={testEmail.trim() && !testEmailOk ? "error" : undefined} />
      <div className="note">
        Uses {previewUser ? <><b>{previewUser.name}</b>'s values (the first recipient)</> : "sample values, as in the preview"}. Normal links go to their real URL; itinerary, app and Useful / Not Useful links use test links. Nothing is tracked or saved.
      </div>
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <Button onClick={() => setTestOpen(false)}>Cancel</Button>
        <Button type="primary" loading={testing} disabled={!testEmailOk} onClick={doTest}>Send</Button>
      </div>
    </CePage>
  );

  const composeRef = useRef(null);
  const fitCompose = useCallback(() => {
    const el = composeRef.current;
    if (!el) return;
    if (window.innerWidth <= 1100) { el.style.height = ""; return; } // one column: the page scrolls
    const box = el.closest("main") || document.scrollingElement;
    const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop;
    el.style.height = `${Math.max(420, box.clientHeight - top - 12)}px`;
  }, []);
  useLayoutEffect(() => {
    fitCompose();
    window.addEventListener("resize", fitCompose);
    return () => window.removeEventListener("resize", fitCompose);
  }, [fitCompose]);

  const jump = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  const step = (n, key, id, label) => (
    <button type="button" className={status[key].cls} onClick={() => jump(id)}>
      <span className="sn">{status[key].cls === "ok" ? "✓" : n}</span>
      <span>{label}<span className="st"> · {status[key].txt}</span></span>
    </button>
  );
  const previewSubject = template?.subject && preview ? renderTemplate(template.subject, preview.custom_data, previewUser?.email).replace(/<[^>]+>/g, "") : "";

  return (
    <CePage>
      <div className="crumb">
        <Tooltip title="Go back">
          <button type="button" className="backbtn" aria-label="Go back" onClick={() => navigate("/email_campaign?tab=custom")}><ArrowLeftOutlined /></button>
        </Tooltip>
        <Tooltip title="Go back to Email Campaigns">
          <button type="button" className="linkbtn" onClick={() => navigate("/email_campaign?tab=custom")}>Email Campaigns</button>
        </Tooltip> / Custom Email / Create
      </div>
      <div className="chead">
        <div>
          <h2>Create Custom Email</h2>
          <div className="sub">Pick a OneSignal template, fill its variables, and choose who gets it. The preview updates as you type.</div>
        </div>
        <Popover open={testOpen} onOpenChange={(o) => setTestOpen(o && !!template && !noSubject)} trigger="click" placement="bottomRight" content={testPopover}>
          <Button icon={<MailOutlined />} disabled={!template || noSubject} title={noSubject ? "This template has no subject" : undefined}>Send test email</Button>
        </Popover>
      </div>

      <nav className="stepper" aria-label="Steps">
        {step(1, "template", "sec-template", "Template")}
        {step(2, "variables", "sec-vars", "Variables")}
        {step(3, "recipients", "sec-recips", "Recipients")}
        {step(4, "send", "step-send", "Review & send")}
      </nav>

      <div className="compose" ref={composeRef}>
        <div className="ce-col">
          <section className="scard" id="sec-template">
            <SectionHead n={1} title="Campaign & template" desc="The title is only shown in the admin panel." status={status.template} />
            <div className="sbody">
              <div className="two">
                <div className="field">
                  <label htmlFor="ce-title">Campaign title</label>
                  <Input id="ce-title" aria-label="Campaign title" maxLength={255} value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. Weekend trip ideas · Oct" />
                </div>
                <div className="field">
                  <label htmlFor="ce-tpl">OneSignal email template</label>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Select id="ce-tpl" aria-label="OneSignal template" showSearch optionFilterProp="label" loading={tplPages.isLoading || tplPages.isFetchingNextPage}
                      style={{ flex: 1, minWidth: 0 }} placeholder="Choose a template" value={templateId}
                      onChange={(v) => { setTemplateId(v); setPreview(null); setPreviewError(null); }}
                      options={[...templates, ...(template && !templates.some((t) => t.id === template.id) ? [template] : [])].map((t) => ({ value: t.id, label: t.name }))}
                      onPopupScroll={(e) => {
                        const t = e.target;
                        if (t.scrollTop + t.offsetHeight >= t.scrollHeight - 20 && tplPages.hasNextPage && !tplPages.isFetchingNextPage) tplPages.fetchNextPage();
                      }}
                      dropdownRender={(menu) => (
                        <>
                          {menu}
                          {tplPages.isFetchingNextPage && <div style={{ textAlign: "center", padding: 6 }}><Spin size="small" /></div>}
                        </>
                      )} />
                    <Button aria-label="Reload templates" title="Reload templates from OneSignal" icon={<ReloadOutlined />} onClick={() => tplPages.refetch()} />
                  </div>
                </div>
              </div>
              {templateId && (
                <div className="tplcard">
                  <span className="tplicon" aria-hidden="true"><MailOutlined /></span>
                  <div>
                    {loadingTemplate || !template ? <Spin size="small" /> : (
                      <>
                        <div><b>{template.name}</b></div>
                        <div className="note"><span className="mono">{template.id}</span> · edited in OneSignal {fmtDay(template.updated_at)}</div>
                      </>
                    )}
                  </div>
                  {template && <span className="pill">{plural(template.variables.length, "variable")}</span>}
                </div>
              )}
              {noSubject && <div className="blocked" style={{ marginTop: 10 }}>This template has no subject in OneSignal, so it can't be sent until one is added there.</div>}
            </div>
          </section>

          <section className="scard" id="sec-vars">
            <SectionHead n={2} title="Variables" status={status.variables} />
            <div className="sbody">
              {!template ? <div className="note">Pick a template first.</div> : !template.variables.length ? (
                <div className="warnbox">No <span className="mono">message.custom_data.*</span> variables in this template. It can still be sent as it is.</div>
              ) : template.variables.map((info) => vars[info.key] && (
                <VariableRow key={`${template.id}-${info.key}`} info={info} v={vars[info.key]} varsByKey={vars} recipientIds={recipientIds}
                  onChange={(nv) => { setVars((prev) => ({ ...prev, [info.key]: nv })); setFocus({ key: info.key, at: Date.now() }); }} />
              ))}
            </div>
          </section>

          <section className="scard" id="sec-recips">
            <SectionHead n={3} title="Recipients" desc="One email per user. Only the users added here get the email." status={status.recipients} />
            <div className="sbody">
              <div className="tofield">
                <label>To</label>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <RemoteSelect ariaLabel="Recipients" mode="multiple" value={recipients} onChange={setRecipients} fetchOptions={fetchUsers} pasteLookup={pasteUsers}
                    placeholder="Search by name, email, id, or paste emails" renderOption={userOption} />
                </div>
              </div>
              {mappedNotRecipients.length > 0 && (
                <div className="warnbox" style={{ marginTop: 8 }}>
                  ⚠ {mappedNotRecipients.length} user{mappedNotRecipients.length > 1 ? "s have" : " has"} an itinerary picked in Variables but {mappedNotRecipients.length > 1 ? "aren't" : "isn't"} in Recipients — {mappedNotRecipients.map((u) => u.name || u.label).join(", ")} will <b>not</b> get this email.
                </div>
              )}
              <div className="note" style={{ marginTop: 4 }}>Note: you can paste a list of email addresses.</div>
            </div>
          </section>

          <section className="scard" id="step-send">
            <SectionHead n={4} title="Review & send" desc="One OneSignal email per user, each with their own values and tracked links." status={status.send} />
            <div className="sbody">
              <dl className="summary">
                <dt>Campaign</dt><dd>{title.trim() || <span className="muted">No title yet</span>}</dd>
                <dt>Template</dt><dd>{template?.name || <span className="muted">None yet</span>}</dd>
                <dt>Subject</dt><dd>{previewSubject || <span className="muted">—</span>} {previewSubject && <span className="muted">({previewUser ? `for ${previewUser.name}` : "sample values"})</span>}</dd>
                <dt>Recipients</dt><dd>{plural(recipients.length, "user")} · {plural(recipients.length, "email")}</dd>
                {itinCount > 0 && <><dt>Itineraries</dt><dd>{plural(itinCount, "download link")} per email</dd></>}
                <dt>Tracked links</dt><dd>{preview ? `${preview.links.length} per email` : "—"}</dd>
              </dl>
              {blockers.length > 0 && <ul className="blockers">{blockers.map((b, i) => <li key={i}>✗ {b}</li>)}</ul>}
              {sending ? (
                <div className="confirm sending" role="status" aria-live="polite">
                  <div className="row" style={{ gap: 12, alignItems: "center" }}>
                    <Spin size="large" />
                    <div>
                      <b>Email is sending…</b>
                      <div className="note" style={{ marginTop: 2 }}>
                        Sending to {plural(recipients.length, "user")}. Please keep this tab open. Don't close or refresh the page until it finishes.
                      </div>
                    </div>
                  </div>
                </div>
              ) : confirming ? (
                <div className="confirm">
                  <b>Send “{title.trim()}” to {plural(recipients.length, "user")}?</b>
                  <div className="note" style={{ margin: "4px 0 10px" }}>One email per user. This can't be undone.</div>
                  <div className="row" style={{ justifyContent: "flex-end" }}>
                    <Button disabled={sending} onClick={() => setConfirming(false)}>Cancel</Button>
                    <Button type="primary" size="large" loading={sending} onClick={doSend}>Send {plural(recipients.length, "email")}</Button>
                  </div>
                </div>
              ) : (
                <div className="sendrow">
                  <Button type="primary" size="large" disabled={!canSend} onClick={() => setConfirming(true)}>Send to {plural(recipients.length, "recipient")}</Button>
                </div>
              )}
            </div>
          </section>
        </div>
        <EmailPreview template={template} preview={preview} loading={previewLoading} error={previewError} previewUser={previewUser}
          mode={mode} onMode={setMode} focus={focus} />
      </div>
    </CePage>
  );
};

export default CreateCustomEmail;
